import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Form, Row, Col, Button, Table, Spinner, Alert, ToggleButtonGroup, ToggleButton, InputGroup } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import Pagination from '../components/Pagination';
import { adminApi, extractError } from '../api/client';
import { US_STATES, PARTIES, OFFICES, STATUS_OPTIONS, fmtRelative } from '../lib/lookups';
import { useStates, useCounties } from '../hooks/useLocations';

export default function SearchPage() {
  const [filters, setFilters] = useState({
    q: '', state: '', district: '', county_id: '', office: '', party: '', election_year: '', status: '', incumbent: '',
  });
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [view, setView] = useState('table');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  const { findByAbbr } = useStates();
  const linkedState = filters.state ? findByAbbr(filters.state) : null;
  const { counties: searchCounties } = useCounties(linkedState?.id);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  function update(field, value) {
    setFilters((f) => ({ ...f, [field]: value }));
  }

  async function runSearch(p = 1) {
    setLoading(true);
    setError('');
    setSearched(true);
    try {
      const params = { page: p, page_size: pageSize };
      if (filters.q.trim()) params.q = filters.q.trim();
      if (filters.state) params.state = filters.state;
      if (filters.district.trim()) params.district = filters.district.trim();
      if (filters.county_id) params.county_id = filters.county_id;
      if (filters.office) params.office = filters.office;
      if (filters.party) params.party = filters.party;
      if (filters.election_year) params.election_year = Number(filters.election_year);
      if (filters.status) params.status = filters.status;
      if (filters.incumbent !== '') params.incumbent = filters.incumbent;
      const res = await adminApi.get('/admin/candidates', { params });
      setResults(res.data.items || []);
      setTotal(res.data.total || 0);
      setPage(p);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }

  function reset() {
    setFilters({ q: '', state: '', district: '', county_id: '', office: '', party: '', election_year: '', status: '', incumbent: '' });
    setResults([]); setTotal(0); setPage(1); setSearched(false);
  }

  function onSubmit(e) {
    e.preventDefault();
    runSearch(1);
  }

  return (
    <>
      <PageHeader title="Advanced search" subtitle="Find candidates across all statuses, parties, and locations" />
      <div className="page-content">
        <Row className="g-3">
          <Col xs={12} lg={4}>
            <Card>
              <Card.Header>Filters</Card.Header>
              <Card.Body>
                <Form onSubmit={onSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Name / keyword</Form.Label>
                    <InputGroup>
                      <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
                      <Form.Control value={filters.q} onChange={(e) => update('q', e.target.value)} data-testid="search-q-input" />
                    </InputGroup>
                  </Form.Group>
                  <Row className="g-2">
                    <Col xs={6}>
                      <Form.Group>
                        <Form.Label>State</Form.Label>
                        <Form.Select value={filters.state} onChange={(e) => { update('state', e.target.value); update('county_id', ''); }} data-testid="search-state-select">
                          <option value="">Any</option>
                          {US_STATES.map(([code, name]) => <option key={code} value={code}>{code}</option>)}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col xs={6}>
                      <Form.Group>
                        <Form.Label>District</Form.Label>
                        <Form.Control value={filters.district} onChange={(e) => update('district', e.target.value)} placeholder="e.g., FL-15" data-testid="search-district-input" />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mt-2">
                    <Form.Label>County</Form.Label>
                    <Form.Select
                      value={filters.county_id}
                      onChange={(e) => update('county_id', e.target.value)}
                      disabled={!linkedState || searchCounties.length === 0}
                      data-testid="search-county-select"
                    >
                      <option value="">
                        {!filters.state ? 'Pick a state first' : !linkedState ? 'State not seeded' : searchCounties.length === 0 ? 'No counties' : 'Any'}
                      </option>
                      {searchCounties.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mt-2">
                    <Form.Label>Office</Form.Label>
                    <Form.Select value={filters.office} onChange={(e) => update('office', e.target.value)} data-testid="search-office-select">
                      <option value="">Any</option>
                      {OFFICES.map((o) => <option key={o} value={o}>{o}</option>)}
                    </Form.Select>
                  </Form.Group>
                  <Row className="g-2 mt-1">
                    <Col xs={6}>
                      <Form.Group>
                        <Form.Label>Party</Form.Label>
                        <Form.Select value={filters.party} onChange={(e) => update('party', e.target.value)} data-testid="search-party-select">
                          <option value="">Any</option>
                          {PARTIES.map((p) => <option key={p} value={p}>{p}</option>)}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col xs={6}>
                      <Form.Group>
                        <Form.Label>Election year</Form.Label>
                        <Form.Control type="number" value={filters.election_year} onChange={(e) => update('election_year', e.target.value)} data-testid="search-year-input" />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="g-2 mt-1">
                    <Col xs={6}>
                      <Form.Group>
                        <Form.Label>Status</Form.Label>
                        <Form.Select value={filters.status} onChange={(e) => update('status', e.target.value)} data-testid="search-status-select">
                          <option value="">Any</option>
                          {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col xs={6}>
                      <Form.Group>
                        <Form.Label>Incumbent</Form.Label>
                        <Form.Select value={filters.incumbent} onChange={(e) => update('incumbent', e.target.value)} data-testid="search-incumbent-select">
                          <option value="">Any</option>
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  <div className="d-flex gap-2 mt-4">
                    <Button type="submit" variant="primary" disabled={loading} data-testid="search-apply-filters-button">
                      {loading ? <Spinner size="sm" animation="border" /> : <><i className="bi bi-search me-1" /> Search</>}
                    </Button>
                    <Button type="button" variant="outline-secondary" onClick={reset} data-testid="search-reset-filters-button">
                      Reset
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} lg={8}>
            <Card data-testid="search-results">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <span>{searched ? `${total} results` : 'Results'}</span>
                <ToggleButtonGroup type="radio" name="view" value={view} onChange={setView} size="sm">
                  <ToggleButton id="view-table" value="table" variant="outline-secondary" data-testid="search-view-table">Table</ToggleButton>
                  <ToggleButton id="view-card" value="card" variant="outline-secondary" data-testid="search-view-card">Cards</ToggleButton>
                </ToggleButtonGroup>
              </Card.Header>
              {error && <Alert variant="danger" className="m-3">{error}</Alert>}
              {!searched ? (
                <div className="empty-state">
                  <i className="bi bi-funnel" />
                  <div>Apply filters to find candidates.</div>
                </div>
              ) : loading ? (
                <div className="text-center p-5"><Spinner animation="border" /></div>
              ) : results.length === 0 ? (
                <div className="empty-state">
                  <i className="bi bi-inbox" />
                  <div>No matching candidates.</div>
                </div>
              ) : view === 'table' ? (
                <>
                  <Table hover responsive size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Name</th><th>Office</th><th>State</th><th>Party</th><th>Year</th><th>Status</th><th>Updated</th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((c) => (
                        <tr key={c.id} data-testid={`search-result-${c.id}`}>
                          <td><Link to={`/candidates/${c.id}`} className="text-decoration-none fw-semibold">{c.full_name}</Link></td>
                          <td>{c.office}</td>
                          <td>{c.state}{c.district ? ` — ${c.district}` : ''}</td>
                          <td>{c.party}</td>
                          <td>{c.election_year}</td>
                          <td><StatusBadge status={c.status} /></td>
                          <td className="text-muted">{fmtRelative(c.updated_at)}</td>
                          <td className="text-end"><Link to={`/candidates/${c.id}`}><Button size="sm" variant="outline-secondary">View</Button></Link></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <div className="d-flex justify-content-between align-items-center p-3" style={{ borderTop: '1px solid var(--border)' }}>
                    <div className="text-muted" style={{ fontSize: '0.85rem' }}>Page {page} of {totalPages} · {total} results</div>
                    <Pagination page={page} totalPages={totalPages} onChange={(p) => runSearch(p)} />
                  </div>
                </>
              ) : (
                <Card.Body>
                  <Row className="g-3">
                    {results.map((c) => (
                      <Col xs={12} md={6} key={c.id}>
                        <Card>
                          <Card.Body>
                            <div className="d-flex gap-3">
                              <div className="avatar-sm" style={{ width: 48, height: 48 }}>
                                {c.photo_url ? <img src={c.photo_url} alt={c.full_name} /> : <i className="bi bi-person" />}
                              </div>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <Link to={`/candidates/${c.id}`} className="text-decoration-none fw-semibold">{c.full_name}</Link>
                                <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                                  {c.office} · {c.state}{c.district ? ` — ${c.district}` : ''} · {c.party}
                                </div>
                                <div className="mt-2 d-flex gap-2 align-items-center">
                                  <StatusBadge status={c.status} />
                                  <span className="text-muted" style={{ fontSize: '0.78rem' }}>{c.election_year}</span>
                                </div>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                  <div className="d-flex justify-content-end mt-3">
                    <Pagination page={page} totalPages={totalPages} onChange={(p) => runSearch(p)} />
                  </div>
                </Card.Body>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
}
